import React, { HTMLAttributes, useMemo, useEffect } from 'react';

import styledC from 'styled-components';

import { usePrevious } from '@adsk/alloy-react-helpers';
import { ChevronDownIcon } from '@adsk/alloy-react-icon';
import theme from '@adsk/alloy-react-theme';

const getChevronAnimation = ({ expanded }: { expanded: boolean }) => {
  const transform = expanded ? 'rotate(0deg)' : 'rotate(-90deg)';

  return {
    transform,
    transition: 'transform 0.15s linear',
  };
};

const StyledParagraph = styledC.p<{ $allowCollapsibleGroups?: boolean }>`
  display: flex;
  align-items: center;
  margin: 0px;
  padding-left: ${theme.spacing.M}px;
  height: 32px;
  background: ${theme.tokens.colors.background.secondary.value};
  color: inherit;
  ${theme.typography.bodySmall}
  ${(props) => props.$allowCollapsibleGroups && 'cursor: pointer;'}
`;

type MenuGroupHeaderProps = HTMLAttributes<HTMLParagraphElement> & {
  allowCollapsibleGroups?: boolean;
  expanded?: boolean;
  scrollToViewVirtual?: () => void;
  isVisibleVirtual?: () => boolean;
};
const MenuGroupHeader = ({
  allowCollapsibleGroups,
  expanded,
  scrollToViewVirtual,
  isVisibleVirtual,
  children,
  ...props
}: MenuGroupHeaderProps) => {
  const chevronAnimation = useMemo(
    () => getChevronAnimation({ expanded: !!expanded }),
    [expanded],
  );
  const prevExpanded = usePrevious(expanded);

  useEffect(() => {
    if (!prevExpanded && expanded) {
      if (!isVisibleVirtual || !isVisibleVirtual()) {
        scrollToViewVirtual?.();
      }
    }
  }, [expanded, isVisibleVirtual, prevExpanded, scrollToViewVirtual]);

  return (
    <StyledParagraph
      $allowCollapsibleGroups={allowCollapsibleGroups}
      {...props}
      data-testid="menu-group-header"
    >
      {allowCollapsibleGroups && (
        <ChevronDownIcon
          style={{
            marginRight: theme.spacing.xxSmall,
            ...chevronAnimation,
          }}
          size={16}
        />
      )}

      {children}
    </StyledParagraph>
  );
};

MenuGroupHeader.displayName = 'MenuGroupHeader';

export default MenuGroupHeader;
